/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../core/viewmodel/ViewModel';
import { StoreProvider } from '../../../stores/StoreProvider';
import * as H from 'history';
import { ChangePasswordStore } from '../../../stores/components/ChangePasswordStore';
import React from 'react';
import { Pages } from '../../../navigation/Pages';
import { SnackbarStore } from '../../../stores/application/SnackbarStore';
import { ResetPasswordService } from '../../../services/ResetPasswordService';
import { RequestState } from '../../../models/components/ForgotPasswordModel';
import { ResetPasswordModel } from '../../../models/components/ResetPasswordModel';

export class ResetPasswordFormViewModel extends ViewModel {
	private resetPasswordModel: ResetPasswordModel = new ResetPasswordModel();
	private readonly changePasswordStore: ChangePasswordStore;
	private readonly snackbarStore: SnackbarStore;
	private readonly passwordService: ResetPasswordService;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.changePasswordStore = storeProvider.changePasswordStore;
		this.snackbarStore = storeProvider.snackbarStore;
		this.passwordService = storeProvider.resetPasswordService;
	}

	public isLoading = (): boolean => {
		return this.resetPasswordModel.state === RequestState.LOADING;
	};

	public getPasswordOne = (): string => {
		return this.changePasswordStore.getPasswordOne();
	};

	public setPasswordOne = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		this.changePasswordStore.setPasswordOne(e.target.value);
	};

	public getPasswordTwo = (): string => {
		return this.changePasswordStore.getPasswordTwo();
	};

	public setPasswordTwo = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		this.changePasswordStore.setPasswordTwo(e.target.value);
	};

	public disableChangeButton = (): boolean => {
		return this.getPasswordOne() === '' || this.getPasswordOne() !== this.getPasswordTwo();
	};

	public cancel = (): void => {
		this.navigateTo(Pages.REQUEST_LOGIN);
	};

	public updatePassword = (code: string | null, password: string, passwordConfirmation: string): void => {
		if (!!code) {
			this.resetPasswordModel.setState(RequestState.LOADING);
			this.passwordService
				.resetPassword(code, password, passwordConfirmation)
				.then(() => {
					this.changePasswordStore.setDidResetPassword(true);
					this.cancel();
					this.resetPasswordModel.setState(RequestState.INIT);
				})
				.catch((error) => {
					if (error.message === 'Network Error') {
						this.snackbarStore.showError('application.network_error');
					} else if (error.request.status === 400) {
						this.snackbarStore.showError('resetPassword.reset_invalid_code');
					}
					this.cancel();
					this.resetPasswordModel.setState(RequestState.INIT);
				});
		} else {
			this.snackbarStore.showError('resetPassword.reset_no_code');
			this.cancel();
		}
	};
}
